import React, { useEffect } from "react";
import { Phone, Mail, MapPin } from "lucide-react";
import { Button, Form, Input, Select, message } from "antd";
import { API_METHODS } from "../../../constants";
import { isEmpty } from "../../../generalFunctions";
import useApi from "../../../hooks/useApi";

const ContactSection = () => {
  const [quickForm] = Form.useForm();
  const [quickResponse, quickRequest] = useApi(
    "/user/contact",
    null,
    API_METHODS.POST
  );
  const [messageApi, contextHolder] = message.useMessage();

  const handleQuickSubmit = (values) => {
    quickRequest(values);
  };

  useEffect(() => {
    if (
      !isEmpty(quickResponse) &&
      !isEmpty(quickResponse.data) &&
      !quickResponse.isLoading
    ) {
      // Check if response has the expected structure

      messageApi.open({
        type: "success",
        content: "Thank you for contacting us!",
        duration: 5,
      });

      quickForm.resetFields();
    } else if (!isEmpty(quickResponse) && !isEmpty(quickResponse.error)) {
      // Handle error case
      messageApi.open({
        type: "error",
        content: `❌ Error in sending request`,
        duration: 4,
      });
    }
  }, [quickResponse, quickForm, messageApi]);

  return (
    <section className="py-8 lg:py-16 px-4 bg-white">
      {contextHolder}
      <div className="container mx-auto max-w-6xl">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start lg:items-center">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 lg:mb-6 text-gray-800">
              🙏 Need Help?
            </h2>
            <p className="text-lg lg:text-xl text-gray-600 mb-6 lg:mb-8 leading-relaxed">
              Our devotee support team is available to help you with puja
              bookings and spiritual guidance for Shree Mahalaxmi Ambabai
              worship.
            </p>

            <div className="space-y-4 lg:space-y-6">
              {[
                {
                  icon: <Phone className="h-5 w-5 lg:h-6 lg:w-6" />,
                  title: "Phone Support",
                  value: "+91 9699588995",
                  action: "Call Now",
                  onClick: () => window.open("tel:+919699588995", "_self"),
                },
                {
                  icon: <Mail className="h-5 w-5 lg:h-6 lg:w-6" />,
                  title: "Email Support",
                  value: "ambabaimahalaxmipuja@gmail.com",
                  action: "Send Email",
                  onClick: () =>
                    window.open(
                      "mailto:ambabaimahalaxmipuja@gmail.com?subject=Puja Inquiry from MahalaxmiPuja Website&body=Hello MahalaxmiPuja Team,%0D%0A%0D%0AI need help with puja booking and spiritual guidance.%0D%0A%0D%0APlease contact me at your earliest convenience.%0D%0A%0D%0AThank you!%0D%0A%0D%0ABest regards",
                      "_blank"
                    ),
                },
                {
                  icon: <MapPin className="h-5 w-5 lg:h-6 lg:w-6" />,
                  title: "Our Address",
                  value:
                    "2818,A Ward, Varuntirth Ves , Shivaji Peth,Kolhapur.416012",
                  action: "Get Directions",
                  onClick: () =>
                    window.open(
                      "https://www.google.com/maps/place/16%C2%B041'33.9%22N+74%C2%B013'15.6%22E/@16.6929139,74.2211889,20.87z/data=!4m4!3m3!8m2!3d16.692756!4d74.221009?entry=ttu&g_ep=EgoyMDI1MDcyMy4wIKXMDSoASAFQAw%3D%3D",
                      "_blank"
                    ),
                },
              ].map((contact, index) => (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 p-4 lg:p-6 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg hover:shadow-lg transition-all transform hover:scale-105"
                >
                  <div className="text-orange-600 self-center sm:self-auto">
                    {contact.icon}
                  </div>
                  <div className="flex-1 text-center sm:text-left">
                    <div className="font-semibold text-gray-800 text-sm lg:text-base">
                      {contact.title}
                    </div>
                    <div className="text-gray-600 text-sm lg:text-base break-all sm:break-normal">
                      {contact.value}
                    </div>
                  </div>
                  <button
                    onClick={contact.onClick}
                    className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-2 lg:px-4 lg:py-2 rounded-lg text-sm lg:text-base font-medium hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg self-center sm:self-auto"
                  >
                    {contact.action}
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200 rounded-xl p-6 lg:p-8 transform hover:scale-105 transition-all shadow-lg">
            <h3 className="text-xl lg:text-2xl font-bold mb-4 lg:mb-6 text-gray-800">
              ⚡ Quick Contact
            </h3>

            <Form
              form={quickForm}
              layout="vertical"
              onFinish={handleQuickSubmit}
              autoComplete="off"
            >
              <div className="space-y-3 lg:space-y-4">
                <Form.Item
                  name="name"
                  rules={[
                    { required: true, message: "Name is required" },
                    { min: 2, message: "Name must be at least 2 characters" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Your Name *"
                    className="px-3 py-2 lg:px-4 lg:py-3 rounded-lg border border-orange-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  rules={[
                    {
                      pattern: /^[6-9]\d{9}$/,
                      message: "Enter valid 10-digit phone number",
                    },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Your Phone Number"
                    className="px-3 py-2 lg:px-4 lg:py-3 rounded-lg border border-orange-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </Form.Item>

                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: "Email is required" },
                    { type: "email", message: "Enter valid email address" },
                  ]}
                >
                  <Input
                    size="large"
                    placeholder="Your Email Address *"
                    className="px-3 py-2 lg:px-4 lg:py-3 rounded-lg border border-orange-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
                  />
                </Form.Item>

                <Form.Item name="subject">
                  <Select
                    size="large"
                    placeholder="Select Your Query Type"
                    className="w-full"
                    style={{
                      borderRadius: "8px",
                    }}
                  >
                    <Select.Option value="puja">Puja Booking</Select.Option>
                    <Select.Option value="guidance">
                      Spiritual Guidance
                    </Select.Option>
                    <Select.Option value="festival">
                      Festival Celebrations
                    </Select.Option>
                    <Select.Option value="technical">
                      Technical Support
                    </Select.Option>
                    <Select.Option value="feedback">
                      Feedback & Suggestions
                    </Select.Option>
                    <Select.Option value="other">Other Questions</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="message"
                  rules={[
                    { required: true, message: "Message is required" },
                    {
                      min: 10,
                      message: "Message must be at least 10 characters",
                    },
                  ]}
                >
                  <Input.TextArea
                    rows={3}
                    placeholder="Your message about Mahalaxmi puja requirements..."
                    className="w-full px-3 py-2 lg:px-4 lg:py-3 rounded-lg border border-orange-300 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all resize-none"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    loading={quickResponse?.isLoading}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 lg:py-3 rounded-lg text-sm lg:text-base font-semibold hover:from-orange-600 hover:to-red-600 transition-all transform hover:scale-105 shadow-lg border-none h-10 lg:h-12"
                  >
                    🚀 Send Message (Quick Response)
                  </Button>
                </Form.Item>

                <div className="text-center text-xs lg:text-sm text-gray-600">
                  ⚡ We respond within 2 hours
                </div>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
